/**
 * jobActivityApi — AISC §1.7.0 Phase 4 read-only Job Activity Log feed.
 *
 * Backed by GET /api/job/:id/activity (Phase 4 jobActivity route).
 */

import ApiCall from "./apicall";

export async function getJobActivity(job_id, filters = {}) {
  const params = {};
  if (filters.since) params.since = filters.since;
  if (filters.until) params.until = filters.until;
  if (filters.event_type) params.event_type = filters.event_type;
  if (filters.limit) params.limit = filters.limit;
  const res = await ApiCall.get(`/job/${job_id}/activity`, { params });
  return res.data; // { count, rows }
}

/**
 * Convenience: derive a friendly event name from reason_category (legacy
 * field-level diff schema) or event_type (production domain-event schema).
 *
 * 2026-05-06 — extended with production event_type taxonomy. The backend
 * route returns event_type values like 'BOLT_TEST_CREATED', 'RCV-01',
 * 'FAB-V01-BLOCK', etc. (verified via SELECT DISTINCT on production
 * audit_log). Without these mappings the JobActivityLog page renders
 * raw token strings; with them it reads like an ops journal.
 *
 * E.g., 'AISC-1-7-0:JOB-RFI-OUT' → 'RFI sent'
 *       'BOLT_TEST_CREATED'      → 'Bolt test recorded'
 *       'AISC-1-7-0:JOB-WORKFLOW-OVERRIDE' → 'Workflow step overridden'
 */
export function describeEvent(reason_category) {
  const rc = String(reason_category || "");
  const map = {
    // ─── AISC §1.7.0 events (rolled-back schema, kept for backcompat) ──
    "AISC-1-7-0:JOB-RFI-DRAFT": "RFI drafted",
    "AISC-1-7-0:JOB-RFI-OUT": "RFI sent",
    "AISC-1-7-0:JOB-RFI-IN": "RFI answered",
    "AISC-1-7-0:JOB-RFI-CLOSED": "RFI closed",
    "AISC-1-7-0:JOB-RFI-CANCELLED": "RFI cancelled",
    "AISC-1-7-0:JOB-TRANSMITTAL-DRAFT": "Transmittal drafted",
    "AISC-1-7-0:JOB-TRANSMITTAL-OUT": "Transmittal sent",
    "AISC-1-7-0:JOB-TRANSMITTAL-IN": "Transmittal returned",
    "AISC-1-7-0:JOB-TRANSMITTAL-CLOSED": "Transmittal closed",
    "AISC-1-7-0:JOB-TRANSMITTAL-CANCELLED": "Transmittal cancelled",
    "AISC-1-7-0:JOB-PIT-CREATED": "Print Issue Ticket created",
    "AISC-1-7-0:JOB-PIT-PRINTED": "Print Issue Ticket printed",
    "AISC-1-7-0:JOB-PIT-DELIVERED": "Print Issue Ticket delivered",
    "AISC-1-7-0:JOB-PIT-CANCELLED": "Print Issue Ticket cancelled",
    "AISC-1-7-0:JOB-WAIVER-REQUESTED": "Customer waiver requested",
    "AISC-1-7-0:JOB-WAIVER-RECEIVED": "Customer waiver received",
    "AISC-1-7-0:JOB-WAIVER-ACTIVE": "Customer waiver active",
    "AISC-1-7-0:JOB-WAIVER-REVOKED": "Customer waiver revoked",
    "AISC-1-7-0:JOB-WORKFLOW-OVERRIDE": "Workflow step overridden",
    "AISC-1-7-0": "Job category / waiver direct change",

    // ─── Receiving / inventory / heat (production domain-event taxonomy) ──
    "RCV-01":  "Material received (heat scan)",
    "RCV-03-BLOCKED": "Receiving blocked — verification failed",
    "RCV-04":  "Material received",
    "RCV-06":  "Receiving label printed",
    "DUPLICATE_HEAT_DETECTED": "Duplicate heat number detected",

    // ─── Issuance ──
    "ISS-01":           "Material issued to fabrication",
    "ISS-V01-BLOCK":    "Issuance blocked — rev-lock",
    "ISS-V02-BLOCK":    "Issuance blocked — ack required",

    // ─── Fabrication / piece advance ──
    "FAB-01":           "Piece advanced",
    "FAB-V01-BLOCK":    "Piece advance blocked — rev-lock",
    "FAB-V02-BLOCK":    "Piece advance blocked — ack required",

    // ─── Bolting / NDT / dry film ──
    "BOLT_TEST_CREATED": "Bolt test recorded",
    "BOLT_TEST_RECORDED": "Bolt test recorded",
    "DFT_READING":       "Dry-film thickness reading",

    // ─── Labels ──
    "LABEL_REPRINT":     "Label reprinted",
    "LABEL_VOID":        "Label voided",
    "LABEL_SCAN_VOIDED": "Voided-label scan attempt",

    // ─── QCI / QA actions ──
    "QCI_ACTION":        "QC inspector action",

    // ─── Documents lifecycle ──
    "DOC-ACCESS-CHECK":  "Document access check (allowed)",
    "DOC-ACCESS-BLOCK":  "Document access blocked",

    // ─── Audit / coatings ──
    "AUDIT_EXPORT":      "Audit log exported",
    "PAINT_TDS_UPLOAD":  "Paint TDS uploaded",
  };
  return map[rc] || rc;
}
